// "Glow" team switcher for the By Team solutions page, on the site's light
// palette: a full-bleed white band washed with a drifting brand-gradient
// mesh, the teams picked from pills (the active one fills with the brand
// gradient and glows), and the team's screenshot playing in a
// gradient-bordered frame under a cursor spotlight, with feature cards
// below. The stage body keeps a constant aspect ratio so switching teams
// never shifts layout. The active team is kept in the URL hash
// (e.g. /byteam#developer) so teams can be linked to.
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ScreenshotFrame } from "./ui";

// Feature card scoped to the band — the shared FeatureCard has its own
// hard-coded surface/spacing, so the band keeps a self-contained card.
function GlassCard({ icon: Icon, title, text, accent = false }) {
  return (
    <div className={`tsp-card ${accent ? "tsp-card--accent" : ""}`}>
      {Icon && (
        <span className="tsp-card__icon">
          <Icon size={22} aria-hidden="true" />
        </span>
      )}
      <h3 className="tsp-card__title">{title}</h3>
      <p className="tsp-card__text">{text}</p>
    </div>
  );
}

export default function TeamSpotlight({ tabs, label = "Teams", eyebrow = "Choose your team" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const cinemaRef = useRef(null);
  const fromHash = tabs.find(t => `#${t.id}` === location.hash);
  const [active, setActive] = useState(fromHash ? fromHash.id : tabs[0].id);

  useEffect(() => {
    if (fromHash) setActive(fromHash.id);
  }, [fromHash]);

  const select = id => {
    setActive(id);
    navigate({ hash: id }, { replace: true });
  };

  // Cursor spotlight: pointer position feeds the --tsp-mx/--tsp-my the CSS
  // paints a radial highlight with, rAF-coalesced like the /workflow grid.
  useEffect(() => {
    const cinema = cinemaRef.current;
    if (!cinema) return;
    let raf = 0;
    const move = e => {
      const rect = cinema.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        cinema.style.setProperty("--tsp-mx", `${x}px`);
        cinema.style.setProperty("--tsp-my", `${y}px`);
      });
    };
    cinema.addEventListener("pointermove", move);
    return () => {
      cinema.removeEventListener("pointermove", move);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Roving tabindex: arrows step through the team words and wrap, Home/End jump.
  const onKeyDown = e => {
    const index = tabs.findIndex(t => t.id === active);
    const steps = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    let next;
    if (e.key in steps) next = (index + steps[e.key] + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    else return;
    e.preventDefault();
    const id = tabs[next].id;
    select(id);
    document.getElementById(`tab-${id}`)?.focus();
  };

  const tab = tabs.find(t => t.id === active);
  const TabIcon = tab.icon;

  return (
    <section className="tsp" aria-label={label}>
      <div className="tsp-band">
        <div className="ft-container">
          <p className="tsp-eyebrow">
            <span className="tsp-eyebrow__tick" aria-hidden="true" />
            {eyebrow}
          </p>

          <div className="tsp-words" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
            {tabs.map((t, i) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  id={`tab-${t.id}`}
                  aria-selected={active === t.id}
                  aria-controls={`panel-${t.id}`}
                  tabIndex={active === t.id ? 0 : -1}
                  className="tsp-pill"
                  onClick={() => select(t.id)}
                >
                  <span className="tsp-pill__num" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {Icon && (
                    <span className="tsp-pill__icon">
                      <Icon size={18} aria-hidden="true" />
                    </span>
                  )}
                  <span className="tsp-pill__label">{t.label}</span>
                </button>
              );
            })}
          </div>

          <div className="tsp-cinema-col" role="tabpanel" id={`panel-${tab.id}`} aria-labelledby={`tab-${tab.id}`}>
            <div className="tsp-cinema" ref={cinemaRef}>
              <div className="tsp-cinema__body">
                {/* All shots stay mounted so switching is a crossfade over the
                    aurora and every image is pre-warmed — no first-switch flash. */}
                {tabs.map(t => (
                  <div
                    key={t.id}
                    className={`tsp-shot ${active === t.id ? "tsp-shot--active" : ""}`}
                    aria-hidden={active !== t.id}
                  >
                    <ScreenshotFrame src={t.image} alt={t.imageAlt} height={t.imageHeight} />
                  </div>
                ))}
              </div>
            </div>

            <div className="tsp-detail" key={tab.id}>
              <h2 className="tsp-detail__title">
                {TabIcon && <TabIcon size={24} aria-hidden="true" />}
                {tab.title}
              </h2>
              {tab.paragraphs.map(p => (
                <p className="tsp-detail__text" key={typeof p === "string" ? p : p.key}>
                  {p}
                </p>
              ))}
              {tab.cards && (
                <div className="tsp-cards">
                  {tab.cards.map(c => (
                    <GlassCard key={c.title} {...c} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
